/**
 * Prototype-only mock schema validation (v2 — per-site data review).
 *
 * Two trigger points on the UI surface use the same helpers in here:
 *   - Configuration page → optional "Run schema pre-check" button
 *   - Execute page → mandatory final validation gate on Start
 *
 * v2 upgrade — instead of a binary pass/fail "is this the same dataset_ref"
 * check, this now produces a per-site **Federated Data Review** with three
 * severity levels, modelled on the backend artifacts:
 *   - Backend_To_Frontend/data_review_popup.html
 *   - Backend_To_Frontend/2026-05-26_tabular_schema_validation_preflight_*.{html,md}
 *
 * Severity:
 *   - PASS — site data aligns with the dataset's target schema
 *   - WARN — diverges but training may still proceed (extra columns ignored,
 *            missing label class, registry review pending)
 *   - FAIL — hard incompatibility, blocks training (missing required feature,
 *            label out of range, feature_dim mismatch, mixed dataset refs)
 *
 * NONE of this reaches a real backend. The schema targets come from
 * DATASET_SCHEMAS (mockDatasetDefinitions.js) and the per-site deviations are
 * the curated CAPABILITY_DEVIATIONS map below. Backend's real preflight is the
 * unbuilt "B4" work item — this is the suggested UI shape for when it ships.
 */

import { getDatasetSchema } from "../data/mockDatasetDefinitions";

// ─── Severity helpers ───────────────────────────────────────────────────────
const SEVERITY_RANK = { pass: 0, warn: 1, fail: 2 };
const worstSeverity = (findings) =>
  findings.reduce(
    (acc, f) => (SEVERITY_RANK[f.severity] > SEVERITY_RANK[acc] ? f.severity : acc),
    "pass"
  );

/**
 * Curated per-capability deviations from the target schema. A capability NOT
 * listed here is assumed to align perfectly (PASS). The listed ones seed the
 * demonstrable WARN / FAIL cases, mirroring the data_review_popup examples.
 *
 * Deviation fields (all optional):
 *   extra_columns       string[]  → WARN "Extra columns ignored"
 *   missing_features    string[]  → FAIL "Missing required feature(s)"
 *   label_out_of_range  {observed_max_index} → FAIL "Label out of range"
 *   missing_label_classes string[] → WARN "Missing label class"
 *   feature_dim_observed number   → FAIL "Feature dimension mismatch" (vs schema.feature_dim)
 *   n_samples           number    → overrides the parsed sample estimate for the review row
 */
const CAPABILITY_DEVIATIONS = {
  // Primary Site Oncology — the discoverable end-to-end demo dataset (5 sites):
  //   sun + tgh = clean PASS, mtl = extra-cols WARN, oicr = readiness WARN
  //   (from its needs_attention status), bccrc = label FAIL + missing-class WARN.
  cap_mtl_tabular_primary: {
    extra_columns: ["new_added_f1", "new_added_f2"],
    n_samples: 6200,
  },
  cap_bccrc_tabular_primary: {
    label_out_of_range: { observed_max_index: 4 }, // num_classes=4 → valid 0..3
    missing_label_classes: ["Prostate (index=3)"],
    n_samples: 5500,
  },

  // OMOP — demonstrates a missing-required-feature FAIL on one site.
  cap_smh_tabular_omop: {
    missing_features: ["er_status"],
    n_samples: 600,
  },
};

/**
 * Build a stable string that identifies the current selection, used to
 * detect "stale" check results when the user edits selections after running
 * a pre-check.
 *
 * @param {string|null} processingType
 * @param {Array<{site_id:string, capability_id:string}>} selectedSites
 * @returns {string} signature
 */
export const computeSelectionSignature = (processingType, selectedSites) => {
  const complete = (selectedSites || [])
    .filter((s) => s && s.site_id && s.capability_id)
    .map((s) => `${s.site_id}::${s.capability_id}`)
    .sort();
  return JSON.stringify({ processingType: processingType || null, sites: complete });
};

/** Parse "11000+" / "800+ nodules" / 1200 → integer sample count (best effort). */
const parseSampleCount = (estimate) => {
  if (typeof estimate === "number") return estimate;
  if (!estimate) return null;
  const m = String(estimate).replace(/,/g, "").match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
};

/** Deterministically split n_samples across num_classes for a label histogram.
 *  Uses a fixed descending ratio so the demo is stable run-to-run. */
const synthLabelHist = (nSamples, numClasses) => {
  if (!nSamples || !numClasses) return null;
  const weights = Array.from({ length: numClasses }, (_, i) => numClasses - i); // n, n-1, …, 1
  const wsum = weights.reduce((a, b) => a + b, 0);
  const hist = {};
  let allocated = 0;
  for (let i = 0; i < numClasses; i += 1) {
    const share =
      i === numClasses - 1
        ? nSamples - allocated
        : Math.round((weights[i] / wsum) * nSamples);
    hist[i] = share;
    allocated += share;
  }
  return hist;
};

/**
 * Run the mock schema validation / federated data review against the current
 * selection.
 *
 * @param {{
 *   selectedSites: Array<{site_id:string, capability_id:string}>,
 *   siteCapabilities: Record<string, Array<object>>,
 *   sites: Array<object>,
 *   processingType: string,
 *   source: "pre_check" | "final",
 * }} args
 * @returns {{
 *   overall: "pass"|"warn"|"fail"|"not_ready",
 *   passed: boolean,            // back-compat: overall === "pass"
 *   blocked: boolean,           // overall === "fail"
 *   code: "not_ready"|"mixed_schemas"|"has_findings"|"ok",
 *   abortReason: string|null,
 *   summary: string,
 *   expectedSchema: string|null,  // back-compat: schema_id
 *   schemaHeader: {schema_id, modality, feature_dim?, num_classes, label_space} | null,
 *   counts: {pass:number, warn:number, fail:number},
 *   siteResults: Array<object>,
 *   source: "pre_check"|"final",
 *   ranAt: string,
 * }}
 */
export const runMockSchemaValidation = ({
  selectedSites,
  siteCapabilities,
  sites,
  processingType,
  source,
}) => {
  const ranAt = new Date().toISOString();
  const safeSource = source === "final" ? "final" : "pre_check";
  const completed = (selectedSites || []).filter(
    (s) => s && s.site_id && s.capability_id
  );

  if (completed.length === 0) {
    return {
      overall: "not_ready",
      passed: false,
      blocked: false,
      code: "not_ready",
      abortReason: null,
      summary: "No site capabilities selected.",
      expectedSchema: null,
      schemaHeader: null,
      counts: { pass: 0, warn: 0, fail: 0 },
      siteResults: [],
      source: safeSource,
      ranAt,
    };
  }

  // Resolve capability + site objects for each selected row.
  const rows = completed.map((s) => {
    const caps = siteCapabilities?.[s.site_id] || [];
    const cap = caps.find((c) => c.capability_id === s.capability_id) || null;
    const site = (sites || []).find((x) => x.site_id === s.site_id) || null;
    return { pair: s, cap, site };
  });

  const refs = new Set(rows.map((r) => r.cap?.dataset_ref).filter(Boolean));
  const sharedRef = refs.size === 1 ? [...refs][0] : null;
  const schema = sharedRef ? getDatasetSchema(sharedRef) : null;

  const schemaHeader = schema
    ? {
        schema_id: schema.schema_id,
        modality: schema.modality,
        feature_dim: schema.feature_dim,
        num_classes: schema.num_classes,
        label_space: schema.label_space,
      }
    : null;

  // ── Rule: mixed dataset_refs → every row FAILs ───────────────────────────
  if (refs.size > 1) {
    const siteResults = rows.map((r) => ({
      site_id: r.pair.site_id,
      site_display_name: r.site?.display_name || r.pair.site_id,
      organization_display_name: r.site?.organization_display_name,
      capability_id: r.cap?.capability_id || r.pair.capability_id,
      dataset_display_name: r.cap?.display_name,
      dataset_ref: r.cap?.dataset_ref || null,
      status: "fail",
      n_samples: parseSampleCount(r.cap?.sample_count_estimate),
      label_hist: null,
      findings: [
        {
          severity: "fail",
          title: "Mixed dataset schemas",
          message: `This run mixes more than one logical dataset (${[...refs].join(
            ", "
          )}). Sites must share a single dataset_ref to validate against one schema.`,
        },
      ],
      // back-compat
      passed: false,
      issue: `Capability dataset_ref: ${r.cap?.dataset_ref || "Not set"}`,
      action: "Pick capabilities that share a single dataset reference.",
    }));
    return {
      overall: "fail",
      passed: false,
      blocked: true,
      code: "mixed_schemas",
      abortReason: "DATA_SCHEMA_VALIDATION_FAIL_PRELAUNCH",
      summary:
        "Selected site datasets are not from the same logical dataset, they cannot share a single schema.",
      expectedSchema: "Mixed (incompatible)",
      schemaHeader: null,
      counts: { pass: 0, warn: 0, fail: rows.length },
      siteResults,
      source: safeSource,
      ranAt,
    };
  }

  // ── Per-site review against the shared schema ────────────────────────────
  const siteResults = rows.map((r) => {
    const cap = r.cap;
    const deviation = (cap && CAPABILITY_DEVIATIONS[cap.capability_id]) || {};
    const findings = [];

    // FAIL — missing required feature(s)
    if (deviation.missing_features?.length) {
      findings.push({
        severity: "fail",
        title: "Missing required feature(s)",
        message: `Required schema feature(s) absent from this site's data: [${deviation.missing_features.join(
          ", "
        )}]. The model cannot be trained without them.`,
      });
    }

    // FAIL — feature dimension mismatch
    if (
      typeof deviation.feature_dim_observed === "number" &&
      schema?.feature_dim &&
      deviation.feature_dim_observed !== schema.feature_dim
    ) {
      findings.push({
        severity: "fail",
        title: "Feature dimension mismatch",
        message: `feature_dim expected=${schema.feature_dim} but this site reports ${deviation.feature_dim_observed}.`,
      });
    }

    // FAIL — label out of range
    if (deviation.label_out_of_range && schema?.num_classes) {
      const observed = deviation.label_out_of_range.observed_max_index;
      findings.push({
        severity: "fail",
        title: "Label out of range",
        message: `Found label index max=${observed} but num_classes=${schema.num_classes}. This indicates labels outside the declared label_space. Training should be blocked.`,
      });
    }

    // WARN — extra columns ignored
    if (deviation.extra_columns?.length) {
      findings.push({
        severity: "warn",
        title: "Extra columns ignored",
        message: `${deviation.extra_columns.length} extra column(s) not in schema were ignored: [${deviation.extra_columns.join(
          ", "
        )}].`,
      });
    }

    // WARN — missing label class
    if (deviation.missing_label_classes?.length) {
      findings.push({
        severity: "warn",
        title: "Missing label class",
        message: `This site has no samples for some label classes (allowed, but reduces learning signal): [${deviation.missing_label_classes.join(
          ", "
        )}].`,
      });
    }

    // WARN — registry readiness flagged (decision: needs_attention → WARN)
    if (cap?.readiness_status === "needs_attention") {
      findings.push({
        severity: "warn",
        title: "Schema review pending",
        message:
          cap.notes ||
          "Site-reported capability flagged for schema review in the registry.",
      });
    }

    const status = worstSeverity(findings);
    const nSamples =
      deviation.n_samples ?? parseSampleCount(cap?.sample_count_estimate);

    return {
      site_id: r.pair.site_id,
      site_display_name: r.site?.display_name || r.pair.site_id,
      organization_display_name: r.site?.organization_display_name,
      capability_id: cap?.capability_id || r.pair.capability_id,
      dataset_display_name: cap?.display_name,
      dataset_ref: cap?.dataset_ref || null,
      status,
      n_samples: nSamples,
      label_hist:
        schema?.modality === "tabular"
          ? synthLabelHist(nSamples, schema?.num_classes)
          : null,
      findings,
      // back-compat fields used by older renderers
      passed: status === "pass",
      issue: findings.find((f) => f.severity === "fail")?.message,
      action:
        status === "fail"
          ? "Resolve the failing check at this site, or pick another capability."
          : undefined,
    };
  });

  const counts = siteResults.reduce(
    (acc, r) => {
      acc[r.status] += 1;
      return acc;
    },
    { pass: 0, warn: 0, fail: 0 }
  );

  const overall = counts.fail > 0 ? "fail" : counts.warn > 0 ? "warn" : "pass";
  const blocked = overall === "fail";

  const summary =
    overall === "fail"
      ? `${counts.fail} of ${siteResults.length} site${
          siteResults.length === 1 ? "" : "s"
        } failed schema validation, training is blocked.`
      : overall === "warn"
      ? `${counts.warn} of ${siteResults.length} site${
          siteResults.length === 1 ? "" : "s"
        } raised warnings, training may proceed.`
      : "All sites align with the expected schema.";

  return {
    overall,
    passed: overall === "pass",
    blocked,
    code: overall === "pass" ? "ok" : "has_findings",
    abortReason: blocked ? "DATA_SCHEMA_VALIDATION_FAIL_PRELAUNCH" : null,
    summary,
    expectedSchema: schema?.schema_id || sharedRef,
    schemaHeader,
    counts,
    siteResults,
    source: safeSource,
    ranAt,
  };
};

/**
 * Compare the current selection signature to a recorded one — used by the
 * UI to detect "stale" pre-check results.
 */
export const isPreCheckStale = (currentSignature, recordedSignature) =>
  recordedSignature !== null &&
  recordedSignature !== undefined &&
  recordedSignature !== currentSignature;
